// Content Moderator → Imports (operator only): events read off
// umanitoba.wat2do.io by the six-hourly sync (api/event-imports-sync.js), and
// the clubs seeded from their directory, both waiting for a yes or a no.
//
// ## Events
//
// Every card is a DRAFT the server re-derives from wat2do's record on each load
// (api/_wat2do.js `buildDraft`): name, Winnipeg date and time, a building and
// room matched against the map, free food, up to three tags. "Review" opens it
// as a form — the operator fixes whatever the matcher got wrong, picks the
// club, and Approve files one UMaps event per ticked date under that club
// (api/event-imports.js). From then on it is an ordinary event, moderated in
// the Events tab.
//
// The location line always shows the club's ORIGINAL words beside our guess and
// how sure we are, because the guess is the part most likely to be wrong and the
// one an attendee would walk to.
//
// ## Clubs
//
// The seeded directory, unverified first, each with the sources its email and
// website were found on: verifying a club here is clicking those links and
// confirming they are the club. Nothing here emails anybody — seeded clubs are
// `createdBy: "umaps"` and hear from us only when the operator sends a sign-in
// link from the Clubs tab.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ago } from './shared.js';

const FILTERS = [
  { id: 'new', label: 'To review' },
  { id: 'changed', label: 'Changed on wat2do' },
  { id: 'approved', label: 'Approved' },
  { id: 'dismissed', label: 'Dismissed' },
];

const LOC_PILL = {
  room: { cls: 'vt-pill-good', text: 'Room matched' },
  building: { cls: 'vt-pill-new', text: 'Building only' },
  'off-campus': { cls: 'vt-pill-warn', text: 'Off campus?' },
  online: { cls: 'vt-pill-dim', text: 'Online' },
  unknown: { cls: 'vt-pill-bad', text: 'Pick a location' },
};

const KIND_LABEL = {
  'union-office': 'UMSU office',
  'university-unit': 'University unit',
  business: 'Business',
  person: 'A person',
  unknown: 'Not sure it’s a club',
};

const fmtDate = (d, t) => {
  if (!d) return 'No date';
  const dt = new Date(`${d}T${t || '12:00'}`);
  const day = dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  return t ? `${day}, ${dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : day;
};

const bucketOf = (it) => {
  if (it.state === 'new') return 'new';
  if (it.state === 'approved' && it.changedAt && it.changedAt > (it.decidedAt || 0)) return 'changed';
  return it.state;
};

export default function EventImports({ token, onExpired, onCount }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [view, setView] = useState('events');
  const [filter, setFilter] = useState('new');
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const r = await api('/api/event-imports', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load imports.'); return; }
    setErr('');
    setData(r.body);
  }, [token, onExpired]);
  useEffect(() => { load(); }, [load]);

  const buckets = useMemo(() => {
    const b = { new: [], changed: [], approved: [], dismissed: [] };
    for (const it of data?.items || []) (b[bucketOf(it)] || b.new).push(it);
    const firstDate = (it) => `${it.draft?.date || '9999'}${it.draft?.time || ''}`;
    for (const k of Object.keys(b)) b[k].sort((a, z) => firstDate(a).localeCompare(firstDate(z)));
    return b;
  }, [data]);

  useEffect(() => { if (data) onCount?.(buckets.new.length + buckets.changed.length); }, [data, buckets, onCount]);

  const post = useCallback(async (key, body) => {
    setBusy(key); setErr(''); setMsg('');
    const r = await api('/api/event-imports', { method: 'POST', token, onExpired, body });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return null; }
    return r.body;
  }, [token, onExpired]);

  const sync = async () => {
    const out = await post('sync', { op: 'sync' });
    if (out) setMsg(out.stored ? `Checked wat2do: ${out.seen} events listed, ${out.added} new, ${out.changed} changed.` : `wat2do wasn't read: ${out.error}`);
    await load();
  };

  const approve = async (it, payload) => {
    const out = await post(it.id, { op: 'approve', id: it.id, ...payload });
    if (!out) return;
    const extra = [out.posterError, out.logoError].filter(Boolean).join(' ');
    setMsg(`Approved “${it.draft.name}” — ${out.events.length} event${out.events.length === 1 ? '' : 's'} added${out.clubVerified ? '' : ', held until the club is verified'}. ${extra}`.trim());
    setOpen(null);
    await load();
  };

  const decide = async (it, op) => {
    if (await post(it.id, { op, id: it.id })) { setOpen(null); await load(); }
  };

  if (!data) return <div className="vt-empty">{err || 'Loading imports…'}</div>;
  const meta = data.meta || {};
  const rows = buckets[filter] || [];

  return (
    <>
      <p className="vt-sub">
        Events found on <a href="https://umanitoba.wat2do.io/events" target="_blank" rel="noopener noreferrer">wat2do</a> and
        clubs seeded from its directory. Nothing here is public until you approve it, and approving never emails a club.
      </p>
      <div className="vt-toolbar">
        <div className="vt-seg" role="group" aria-label="Show">
          <button aria-pressed={view === 'events'} onClick={() => setView('events')}>Events</button>
          <button aria-pressed={view === 'clubs'} onClick={() => setView('clubs')}>Clubs</button>
        </div>
        <span className="vt-grow" />
        <span className="vt-meta" style={{ marginTop: 0 }}>
          {meta.lastSyncAt ? `Last checked ${ago(meta.lastSyncAt)}` : 'Never checked yet'}
          {meta.lastSyncAt && !meta.lastSyncOk ? ' — failed' : ''}
        </span>
        <button className="vt-btn vt-small" disabled={busy === 'sync'} onClick={sync}>
          {busy === 'sync' ? 'Checking…' : 'Check wat2do now'}
        </button>
      </div>
      {meta.lastSyncAt && !meta.lastSyncOk && meta.lastSyncError && (
        <div className="vt-note" style={{ marginBottom: 12 }}>
          The last check didn’t read wat2do: {meta.lastSyncError}. The queue below is what the check before it found.
        </div>
      )}
      {err && <div className="vt-err">{err}</div>}
      {msg && <div className="vt-ok">{msg}</div>}

      {view === 'clubs' ? (
        <ClubVerify token={token} onExpired={onExpired} clubs={data.clubs || []} unseeded={data.unseeded || 0} reload={load} />
      ) : (
        <>
          <div className="vt-tabs" role="group" aria-label="Imports">
            {FILTERS.map((f) => (
              <button key={f.id} className="vt-tab" aria-pressed={filter === f.id} onClick={() => { setFilter(f.id); setOpen(null); }}>
                {f.label}
                {buckets[f.id].length > 0 && <span className={`vt-count${f.id === 'new' || f.id === 'changed' ? ' vt-count-hot' : ''}`}>{buckets[f.id].length}</span>}
              </button>
            ))}
          </div>
          <div className="vt-list">
            {rows.length ? rows.map((it) => (
              <ImportRow
                key={it.id} it={it} clubs={data.clubs || []} buildings={data.buildings || []}
                token={token} onExpired={onExpired} reload={load}
                open={open === it.id} busy={busy === it.id}
                onToggle={() => setOpen(open === it.id ? null : it.id)}
                onApprove={(p) => approve(it, p)}
                onDismiss={() => decide(it, 'dismiss')}
                onRestore={() => decide(it, 'restore')}
              />
            )) : <div className="vt-empty">{filter === 'new' ? 'Nothing waiting. 🎉' : 'Nothing here.'}</div>}
          </div>
        </>
      )}
    </>
  );
}

function ImportRow({ it, clubs, buildings, open, busy, onToggle, onApprove, onDismiss, onRestore, token, onExpired, reload }) {
  const { src = {}, draft = {} } = it;
  const pill = LOC_PILL[draft.location?.status] || LOC_PILL.unknown;
  const club = clubs.find((c) => c.username === (it.owner || it.match?.username));
  const more = (draft.upcoming?.length || 0) - 1;
  return (
    <div className="vt-row">
      <div className="vt-rowhead">
        {src.source_image_url
          ? <img className="vt-logo" style={{ objectFit: 'cover' }} src={src.source_image_url} alt="" loading="lazy" />
          : <span className="vt-logoNone" aria-hidden="true">📅</span>}
        <div className="vt-grow">
          <div className="vt-title">
            {draft.name || src.title}{' '}
            <span className={`vt-pill ${pill.cls}`}>{pill.text}</span>
            {src.cancelled && <span className="vt-pill vt-pill-bad"> Cancelled</span>}
            {it.missingSince && <span className="vt-pill vt-pill-dim"> No longer on wat2do</span>}
          </div>
          <div className="vt-meta">
            {fmtDate(draft.date, draft.time)}{more > 0 ? ` · +${more} more date${more === 1 ? '' : 's'}` : ''}
            {' · '}{club ? club.displayName : src.club}{src.ig_handle ? ` (@${src.ig_handle})` : ''}
            {club && !club.approved ? ' · club not verified' : ''}
            {!club ? ' · no UMaps club matched' : ''}
          </div>
          <div className="vt-meta">📍 {src.location || 'no location'}{draft.buildingName ? ` → ${draft.buildingName}${draft.roomNumber ? ` ${draft.roomNumber}` : ''}` : ''}</div>
          {it.duplicates?.length > 0 && (
            <div className="vt-meta" style={{ color: '#b06000' }}>
              Possible duplicate: {it.duplicates.map((d) => `“${d.name}” ${fmtDate(d.date, d.time)}`).join('; ')}
            </div>
          )}
          {bucketOf(it) === 'changed' && <ChangedNote it={it} />}
          {it.state === 'approved' && <div className="vt-meta">Approved {ago(it.decidedAt)} → {it.eventIds?.length || 0} event(s), now in the Events tab.</div>}
        </div>
        <div className="vt-actions">
          {it.state === 'new' && <button className="vt-btn vt-primary vt-small" onClick={onToggle}>{open ? 'Close' : 'Review'}</button>}
          {it.state === 'new' && <button className="vt-btn vt-small" disabled={busy} onClick={onDismiss}>Dismiss</button>}
          {it.state === 'dismissed' && <button className="vt-btn vt-small" disabled={busy} onClick={onRestore}>Back to review</button>}
          {src.source_url && <a className="vt-btn vt-ghost vt-small" href={src.source_url} target="_blank" rel="noopener noreferrer">Post ↗</a>}
        </div>
      </div>
      {open && it.state === 'new' && (
        <div className="vt-rowbody">
          <ReviewForm it={it} clubs={clubs} buildings={buildings} busy={busy} onApprove={onApprove} token={token} onExpired={onExpired} reload={reload} />
        </div>
      )}
    </div>
  );
}

/** What wat2do changed after the operator approved it. */
function ChangedNote({ it }) {
  const was = it.decidedSrc || {};
  const now = it.src || {};
  const what = [];
  if (was.title !== now.title) what.push(`name (“${was.title}” → “${now.title}”)`);
  if (was.location !== now.location) what.push(`location (“${was.location}” → “${now.location}”)`);
  if (JSON.stringify(was.occurrences) !== JSON.stringify(now.occurrences)) what.push('dates');
  if (!was.cancelled && now.cancelled) what.push('it was CANCELLED');
  if (was.description !== now.description) what.push('description');
  return (
    <div className="vt-meta" style={{ color: '#b06000' }}>
      Changed on wat2do {ago(it.changedAt)} since you approved it: {what.join(', ') || 'details'}. Edit the event in the Events tab if it matters.
    </div>
  );
}

function ReviewForm({ it, clubs, buildings, busy, onApprove, token, onExpired, reload }) {
  const { draft = {}, src = {} } = it;
  const [owner, setOwner] = useState(it.match?.username || '');
  const [name, setName] = useState(draft.name || '');
  const [dates, setDates] = useState(() => (draft.upcoming?.length ? draft.upcoming : [{ date: draft.date, time: draft.time, endTime: draft.endTime }])
    .map((o, i) => ({ ...o, on: i === 0 })));
  const [mode, setMode] = useState(draft.offCampus || draft.location?.status === 'online' ? 'off' : 'campus');
  const [building, setBuilding] = useState(draft.buildingSlug || '');
  const [room, setRoom] = useState(draft.roomNumber || '');
  const [address, setAddress] = useState(draft.offCampus?.address || (draft.location?.status === 'online' ? 'Online' : ''));
  const [freeFood, setFreeFood] = useState(!!draft.freeFood);
  const [tags, setTags] = useState((draft.customTags || []).join(', '));
  const [description, setDescription] = useState(draft.description || '');
  const [poster, setPoster] = useState(!!draft.posterUrl);
  const club = clubs.find((c) => c.username === owner);
  const [verify, setVerify] = useState(true);

  const sortedClubs = useMemo(() => [...clubs].sort((a, z) => a.displayName.localeCompare(z.displayName)), [clubs]);
  const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);
  const badTag = tagList.find((t) => Array.from(t).length > 15);
  const chosen = dates.filter((d) => d.on);
  const setDate = (i, patch) => setDates((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const submit = () => onApprove({
    owner,
    verifyClub: !!club && !club.approved && verify,
    dates: chosen.map(({ date, time, endTime }) => ({ date, time, endTime })),
    draft: {
      name, freeFood, description, includePoster: poster,
      customTags: tagList.slice(0, 3),
      ...(mode === 'off'
        ? { offCampus: { address } }
        : { buildingSlug: building, roomNumber: room.trim() }),
    },
  });

  return (
    <>
      {draft.location?.note && <div className="vt-note" style={{ marginBottom: 12 }}>{draft.location.note}</div>}

      <div className="vt-field">
        <label className="vt-label" htmlFor={`own-${it.id}`}>Club</label>
        <select id={`own-${it.id}`} className="vt-input vt-select" value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">— pick the club —</option>
          {sortedClubs.map((c) => <option key={c.username} value={c.username}>{c.displayName}{c.approved ? '' : ' (not verified)'}</option>)}
        </select>
        <div className="vt-hint">
          wat2do says: {src.club}{src.ig_handle ? ` (@${src.ig_handle})` : ''}
          {it.match?.how === 'instagram' ? ' — matched by Instagram.' : it.match?.how === 'name' ? ' — matched by name.' : ' — no match; seed the club or pick one.'}
        </div>
        {club && <ClubFacts club={club} token={token} onExpired={onExpired} onSaved={reload} />}
        {club && !club.approved && (
          <label className="vt-hint" style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'inherit' }}>
            <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} />
            Verify this club too (otherwise the event is held off the map until it is)
          </label>
        )}
      </div>

      <div className="vt-field">
        <label className="vt-label" htmlFor={`nm-${it.id}`}>Name</label>
        <input id={`nm-${it.id}`} className="vt-input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="vt-field">
        <span className="vt-label">When {dates.length > 1 ? '(tick each date to add — one event per date)' : ''}</span>
        {draft.timeNote && <div className="vt-hint" style={{ marginTop: 0, marginBottom: 6, color: '#b06000' }}>⏰ {draft.timeNote}</div>}
        {dates.map((d, i) => (
          <div key={d.id || i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <input type="checkbox" checked={d.on} aria-label="Add this date" onChange={(e) => setDate(i, { on: e.target.checked })} />
            <input className="vt-input" style={{ width: 150 }} type="date" value={d.date || ''} onChange={(e) => setDate(i, { date: e.target.value })} />
            <input className="vt-input" style={{ width: 110 }} type="time" value={d.time || ''} onChange={(e) => setDate(i, { time: e.target.value })} />
            <span className="vt-hint" style={{ marginTop: 0 }}>to</span>
            <input className="vt-input" style={{ width: 110 }} type="time" value={d.endTime || ''} onChange={(e) => setDate(i, { endTime: e.target.value })} />
          </div>
        ))}
      </div>

      <div className="vt-field">
        <span className="vt-label">Where</span>
        <div className="vt-hint" style={{ marginTop: 0, marginBottom: 6 }}>The post says: “{src.location || '—'}”{src.map_location && src.map_location !== src.location ? ` · wat2do’s map: “${src.map_location}”` : ''}</div>
        <div className="vt-seg" role="group" aria-label="Location kind" style={{ marginBottom: 8 }}>
          <button aria-pressed={mode === 'campus'} onClick={() => setMode('campus')}>On campus</button>
          <button aria-pressed={mode === 'off'} onClick={() => setMode('off')}>Off campus / online</button>
        </div>
        {mode === 'campus' ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select className="vt-input vt-select" style={{ maxWidth: 280 }} value={building} onChange={(e) => setBuilding(e.target.value)} aria-label="Building">
              <option value="">— building —</option>
              {buildings.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
            <input className="vt-input" style={{ width: 120 }} placeholder="Room (optional)" value={room} onChange={(e) => setRoom(e.target.value)} aria-label="Room number" />
          </div>
        ) : (
          <input className="vt-input" placeholder="Address, or “Online”" value={address} maxLength={200} onChange={(e) => setAddress(e.target.value)} aria-label="Address" />
        )}
      </div>

      <div className="vt-field">
        <label className="vt-label" htmlFor={`tg-${it.id}`}>Tags (up to 3, comma-separated, 15 characters each)</label>
        <input id={`tg-${it.id}`} className="vt-input" value={tags} onChange={(e) => setTags(e.target.value)} />
        {badTag && <div className="vt-hint" style={{ color: '#c5221f' }}>“{badTag}” is over 15 characters and would be dropped.</div>}
        <label className="vt-hint" style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'inherit' }}>
          <input type="checkbox" checked={freeFood} onChange={(e) => setFreeFood(e.target.checked)} /> Free food
          {Array.isArray(src.food) && src.food.length > 0 ? ` (post mentions: ${src.food.join(', ')}${src.price ? `; costs $${src.price}` : ''})` : ''}
        </label>
      </div>

      <div className="vt-field">
        <label className="vt-label" htmlFor={`ds-${it.id}`}>Description</label>
        <textarea id={`ds-${it.id}`} className="vt-input vt-textarea" rows={6} value={description} maxLength={4000} onChange={(e) => setDescription(e.target.value)} />
      </div>

      {draft.posterUrl && (
        <div className="vt-field">
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={poster} onChange={(e) => setPoster(e.target.checked)} />
            <span>
              <span className="vt-label" style={{ margin: 0 }}>Use the poster</span>
              <a href={draft.posterUrl} target="_blank" rel="noopener noreferrer">
                <img src={draft.posterUrl} alt="The event poster" style={{ display: 'block', maxWidth: 180, borderRadius: 8, marginTop: 6 }} />
              </a>
              <span className="vt-hint">Copied into UMaps’ own storage when you approve.</span>
            </span>
          </label>
        </div>
      )}

      <div className="vt-actions" style={{ justifyContent: 'flex-start' }}>
        <button
          className="vt-btn vt-good"
          disabled={busy || !owner || !name.trim() || !chosen.length || (mode === 'campus' && !building)}
          onClick={submit}
        >
          {busy ? 'Approving…' : `Approve${chosen.length > 1 ? ` ${chosen.length} dates` : ''}`}
        </button>
        {!owner && <span className="vt-hint">Pick the club first.</span>}
        {owner && mode === 'campus' && !building && <span className="vt-hint">Pick a building, or switch to off campus.</span>}
      </div>
    </>
  );
}

/** Everything we know about a seeded club, each fact with where it came from —
 *  and, with `token`, the email and website editable in place, so a wrong or
 *  missing contact is fixed and the club verified without leaving the card. */
function ClubFacts({ club, token, onExpired, onSaved }) {
  const s = club.seed || {};
  const src = s.sources || {};
  const link = (href, text) => (href ? <a href={href} target="_blank" rel="noopener noreferrer">{text}</a> : null);
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const open = (prefillEmail) => {
    setEmail(prefillEmail ?? club.email ?? '');
    setWebsite(club.website || '');
    setErr(''); setDone(''); setEditing(true);
  };

  const save = async (verify) => {
    const nextEmail = email.trim().toLowerCase();
    const emailChanged = nextEmail !== String(club.email || '').toLowerCase();
    // A club that has signed in owns its inbox: moving the address moves its login.
    if (emailChanged && club.firstSignInAt && !window.confirm(
      `${club.displayName} has signed in before. Changing its email moves its login to ${nextEmail || 'nowhere'} — whoever reads the old inbox can no longer sign in. Change it?`)) return;
    setBusy(true); setErr('');
    const body = { op: 'club-contact', username: club.username, website: website.trim(), ...(emailChanged ? { email: nextEmail } : {}), ...(verify ? { verify: true } : {}) };
    const r = await api('/api/event-imports', { method: 'POST', token, onExpired, body });
    setBusy(false);
    if (!r.ok) { setErr(r.body.error || 'That did not save.'); return; }
    setEditing(false);
    setDone(`Saved${r.body.verified && verify ? ' and verified' : ''}.${r.body.logoError ? ` ${r.body.logoError}` : ''}`);
    await onSaved?.();
  };

  return (
    <>
      <dl className="vt-facts" style={{ marginTop: 8, marginBottom: 6 }}>
        <dt>Status</dt>
        <dd>
          {club.approved ? <span className="vt-pill vt-pill-good">Verified</span> : <span className="vt-pill vt-pill-warn">Not verified</span>}
          {s.kind && s.kind !== 'club' && <> <span className="vt-pill vt-pill-bad">{KIND_LABEL[s.kind] || s.kind}</span></>}
          {s.confidence && s.confidence !== 'high' && <> <span className="vt-pill vt-pill-dim">{s.confidence} confidence</span></>}
          {club.firstSignInAt ? ` · signed in ${ago(club.firstSignInAt)}` : ' · never signed in'}
        </dd>
        <dt>Email</dt>
        <dd>
          {club.email || <i>none on the account</i>}{club.email && src.email && !club.foundEmail ? <> · {link(src.email, 'source ↗')}</> : null}
          {club.foundEmail && (
            <div className="vt-hint">
              Found since this club was created: <b>{club.foundEmail}</b>{src.email ? <> · {link(src.email, 'source ↗')}</> : null}.
              It isn’t on the account yet.
              {token && <> <button className="vt-btn vt-small" style={{ marginLeft: 4 }} onClick={() => open(club.foundEmail)}>Use it</button></>}
            </div>
          )}
        </dd>
        <dt>Website</dt>
        <dd>{club.website ? link(club.website, club.website.replace(/^https?:\/\//, '')) : <i>none found</i>}{club.website && src.website && src.website !== club.website ? <> · {link(src.website, 'source ↗')}</> : null}</dd>
        <dt>Instagram</dt>
        <dd>{club.instagram ? link(`https://instagram.com/${club.instagram}`, `@${club.instagram}`) : '—'}</dd>
        {s.personalEmails?.length > 0 && (
          <>
            <dt>Personal</dt>
            <dd>{s.personalEmails.map((pe) => <span key={pe.email}>{pe.email} {link(pe.source, '↗')} </span>)}<span className="vt-hint">Not used as the login — an exec’s own inbox.</span></dd>
          </>
        )}
        {s.notes && (<><dt>Notes</dt><dd>{s.notes}</dd></>)}
      </dl>

      {token && !editing && (
        <div className="vt-actions" style={{ justifyContent: 'flex-start', marginBottom: 6 }}>
          <button className="vt-btn vt-small" onClick={() => open()}>Edit email &amp; website</button>
          {done && <span className="vt-hint" style={{ marginTop: 0, color: '#137333' }}>{done}</span>}
        </div>
      )}
      {token && editing && (
        <div className="vt-card" style={{ padding: 12, marginBottom: 8 }}>
          <div className="vt-field" style={{ marginBottom: 10 }}>
            <label className="vt-label" htmlFor={`em-${club.username}`}>Sign-in email</label>
            <input id={`em-${club.username}`} className="vt-input" type="email" value={email} placeholder="club@umanitoba.ca"
                   onChange={(e) => setEmail(e.target.value)} />
            <div className="vt-hint">The club’s login: its sign-in links go here. Use a club-run inbox, not an exec’s own. Empty removes it.</div>
          </div>
          <div className="vt-field" style={{ marginBottom: 10 }}>
            <label className="vt-label" htmlFor={`ws-${club.username}`}>Website</label>
            <input id={`ws-${club.username}`} className="vt-input" value={website} placeholder="umsu.ca/club-name"
                   onChange={(e) => setWebsite(e.target.value)} />
          </div>
          {err && <div className="vt-err">{err}</div>}
          <div className="vt-actions" style={{ justifyContent: 'flex-start' }}>
            {!club.approved && <button className="vt-btn vt-good vt-small" disabled={busy} onClick={() => save(true)}>{busy ? 'Saving…' : 'Save & verify'}</button>}
            <button className="vt-btn vt-small" disabled={busy} onClick={() => save(false)}>Save</button>
            <button className="vt-btn vt-ghost vt-small" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}

function ClubVerify({ token, onExpired, clubs, unseeded, reload }) {
  const [q, setQ] = useState('');
  const [show, setShow] = useState('todo');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [openId, setOpenId] = useState(null);

  const seeded = clubs.filter((c) => c.seed);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return seeded
      .filter((c) => (show === 'todo' ? !c.approved : show === 'verified' ? c.approved : true))
      .filter((c) => !needle || `${c.displayName} ${c.instagram} ${c.email}`.toLowerCase().includes(needle))
      // Clubs with something to check first, then the ones with nothing to go on.
      .sort((a, z) => (!!z.email - !!a.email) || (!!z.website - !!a.website) || a.displayName.localeCompare(z.displayName));
  }, [seeded, q, show]);

  const verify = async (c, approved) => {
    setBusy(c.username); setErr(''); setMsg('');
    const r = await api('/api/event-imports', { method: 'POST', token, onExpired, body: { op: 'verify-club', username: c.username, approved } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return; }
    setMsg(`${c.displayName} is ${approved ? 'verified' : 'no longer verified'}. ${r.body.logoError || ''}`.trim());
    await reload();
  };

  const seed = async () => {
    setBusy('seed'); setErr(''); setMsg('');
    const r = await api('/api/accounts', { method: 'POST', token, onExpired, body: { op: 'seed-clubs' } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'Seeding did not go through.'); return; }
    setMsg(`Created ${r.body.created?.length || 0} club accounts (${(r.body.created || []).filter((c) => c.canInvite).length} with an email). ${r.body.skipped?.length || 0} already existed or were skipped.`);
    await reload();
  };

  const counts = {
    todo: seeded.filter((c) => !c.approved).length,
    verified: seeded.filter((c) => c.approved).length,
    noContact: seeded.filter((c) => !c.email && !c.website).length,
  };

  return (
    <>
      {err && <div className="vt-err">{err}</div>}
      {msg && <div className="vt-ok">{msg}</div>}
      {unseeded > 0 && (
        <div className="vt-note" style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="vt-grow">{unseeded} club{unseeded === 1 ? '' : 's'} in the seed list {unseeded === 1 ? 'has' : 'have'} no UMaps account yet. Creating them sends no email; each waits here, unverified.</span>
          <button className="vt-btn vt-small vt-primary" disabled={busy === 'seed'} onClick={seed}>{busy === 'seed' ? 'Creating…' : `Create ${unseeded} club accounts`}</button>
        </div>
      )}
      <div className="vt-stats" style={{ marginBottom: 12 }}>
        <span><b>{counts.todo}</b>to verify</span>
        <span><b>{counts.verified}</b>verified</span>
        <span><b>{counts.noContact}</b>no email or website</span>
      </div>
      <div className="vt-toolbar">
        <div className="vt-seg" role="group" aria-label="Which clubs">
          <button aria-pressed={show === 'todo'} onClick={() => setShow('todo')}>To verify</button>
          <button aria-pressed={show === 'verified'} onClick={() => setShow('verified')}>Verified</button>
          <button aria-pressed={show === 'all'} onClick={() => setShow('all')}>All</button>
        </div>
        <input className="vt-input" placeholder="Search name, @handle, email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search clubs" />
      </div>
      <p className="vt-hint" style={{ marginTop: 0, marginBottom: 10 }}>
        Verify a club when its sources check out: the email and website pages are the club’s own, and they link the same Instagram.
        Sign-in links and email changes are in the <b>Clubs</b> tab.
      </p>
      <div className="vt-list">
        {list.length ? list.map((c) => (
          <div key={c.username} className="vt-row">
            <div className="vt-rowhead">
              {c.logo ? <img className="vt-logo" src={c.logo} alt="" loading="lazy" /> : <span className="vt-logoNone" aria-hidden="true">🎪</span>}
              <div className="vt-grow">
                <div className="vt-title">
                  {c.displayName}{' '}
                  {!c.email && !c.website && <span className="vt-pill vt-pill-dim">No contact</span>}
                  {c.seed?.kind && c.seed.kind !== 'club' && <> <span className="vt-pill vt-pill-bad">{KIND_LABEL[c.seed.kind] || c.seed.kind}</span></>}
                </div>
                <div className="vt-meta">{[c.email, c.website && c.website.replace(/^https?:\/\//, ''), c.instagram && `@${c.instagram}`].filter(Boolean).join(' · ')}</div>
              </div>
              <div className="vt-actions">
                <button className="vt-btn vt-small" onClick={() => setOpenId(openId === c.username ? null : c.username)}>{openId === c.username ? 'Hide' : 'Sources & edit'}</button>
                {c.approved
                  ? <button className="vt-btn vt-small vt-danger" disabled={busy === c.username} onClick={() => verify(c, false)}>Unverify</button>
                  : <button className="vt-btn vt-small vt-good" disabled={busy === c.username} onClick={() => verify(c, true)}>Verify</button>}
              </div>
            </div>
            {openId === c.username && <div className="vt-rowbody"><ClubFacts club={c} token={token} onExpired={onExpired} onSaved={reload} /></div>}
          </div>
        )) : <div className="vt-empty">{seeded.length ? 'No clubs match.' : 'No seeded clubs yet.'}</div>}
      </div>
    </>
  );
}
